import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { Zap, Gauge, AlertTriangle, Timer, TrendingUp, Loader2 } from "lucide-react";

const MAX_C = 200;
const MAX_R = 3000;

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`load-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="label-mono text-slate-400">{label}</span>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="mt-3 font-display font-extrabold text-2xl text-ink">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function LoadTestPanel() {
  const [concurrency, setConcurrency] = useState(50);
  const [total, setTotal] = useState(1000);
  const [running, setRunning] = useState(false);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/load-tests");
      setHistory(data);
      if (data[0]) setLatest((prev) => prev || data[0]);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async () => {
    setRunning(true);
    setLatest(null);
    try {
      const c = Math.min(MAX_C, Math.max(1, Number(concurrency) || 1));
      const t = Math.min(MAX_R, Math.max(10, Number(total) || 10));
      const { data } = await api.post("/admin/load-test", { concurrency: c, total_requests: t });
      setLatest(data);
      toast.success(`Load test complete — ${data.requests_run} requests, p95 ${data.p95_ms} ms`);
      loadHistory();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, "Load test failed"));
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-5" data-testid="loadtest-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="label-mono text-slate-400 mb-4 flex items-center gap-1.5"><Zap size={14} className="text-amber-500" /> Run a load test</div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <div className="text-slate-500 mb-1">Concurrent users <span className="text-slate-400">(max {MAX_C})</span></div>
            <Input type="number" min={1} max={MAX_C} value={concurrency} onChange={(e) => setConcurrency(e.target.value)} className="w-40 h-11" data-testid="load-concurrency-input" />
          </label>
          <label className="text-sm">
            <div className="text-slate-500 mb-1">Total requests <span className="text-slate-400">(max {MAX_R})</span></div>
            <Input type="number" min={10} max={MAX_R} value={total} onChange={(e) => setTotal(e.target.value)} className="w-40 h-11" data-testid="load-total-input" />
          </label>
          <Button onClick={run} disabled={running} className="h-11 bg-ink hover:bg-slate-800 text-white px-6" data-testid="run-load-test-btn">
            {running ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> Running…</> : <><Zap size={16} className="mr-1.5" /> Run test</>}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Ramps concurrency in steps against a live API endpoint inside your app and measures latency + errors. Capped for safety so it never harms the live site.
        </p>
      </div>

      {latest && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Timer} label="Avg response" value={`${latest.avg_ms} ms`} accent="#2563EB" />
            <Stat icon={Gauge} label="p95 / p99" value={`${latest.p95_ms} / ${latest.p99_ms} ms`} accent="#7c3aed" />
            <Stat icon={AlertTriangle} label="Error rate" value={`${latest.error_rate}%`} accent={latest.error_rate > 2 ? "#DC2626" : "#059669"} />
            <Stat icon={TrendingUp} label="Throughput" value={`${latest.throughput_rps}/s`} sub={`${latest.requests_run} reqs in ${latest.duration_s}s`} accent="#059669" />
            <Stat icon={Zap} label="Max stable users" value={latest.max_stable_concurrency} sub="before latency/errors degraded" accent="#D97706" />
            <Stat icon={Timer} label="Min / Max" value={`${latest.min_ms} / ${latest.max_ms} ms`} accent="#0ea5e9" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="load-steps-chart">
            <div className="label-mono text-slate-400 mb-4">Response time &amp; errors vs concurrency</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={latest.steps} margin={{ top: 5, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="concurrency" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} label={{ value: "concurrent users", position: "insideBottom", offset: -4, fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={44} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={38} unit="%" />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="l" type="monotone" dataKey="p95_ms" name="p95 (ms)" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="l" type="monotone" dataKey="avg_ms" name="avg (ms)" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="r" type="monotone" dataKey="error_rate" name="errors (%)" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="load-history">
        <div className="label-mono text-slate-400 p-5 pb-3">Past runs</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="px-5 py-2 font-medium">When</th>
              <th className="px-5 py-2 font-medium">Users</th>
              <th className="px-5 py-2 font-medium">Requests</th>
              <th className="px-5 py-2 font-medium">Avg</th>
              <th className="px-5 py-2 font-medium">p95</th>
              <th className="px-5 py-2 font-medium">p99</th>
              <th className="px-5 py-2 font-medium">Errors</th>
              <th className="px-5 py-2 font-medium">Max stable</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">No load tests run yet</td></tr>}
            {history.map((h) => (
              <tr key={h.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => setLatest(h)} data-testid={`load-run-${h.id}`}>
                <td className="px-5 py-2.5 text-slate-500">{new Date(h.created_at).toLocaleString()}</td>
                <td className="px-5 py-2.5">{h.params?.concurrency}</td>
                <td className="px-5 py-2.5">{h.requests_run}</td>
                <td className="px-5 py-2.5">{h.avg_ms} ms</td>
                <td className="px-5 py-2.5">{h.p95_ms} ms</td>
                <td className="px-5 py-2.5">{h.p99_ms} ms</td>
                <td className={`px-5 py-2.5 ${h.error_rate > 2 ? "text-crimson" : "text-emerald-600"}`}>{h.error_rate}%</td>
                <td className="px-5 py-2.5 font-medium">{h.max_stable_concurrency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
